/**
 * @description Renders the knowledge center page
 */

import React from 'react';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import BodyContainer from '@seccon/shared/ui/components/BodyContainer';
import NoResults from '@seccon/shared/ui/components/NoResults';

import CategoryList from './components/CategoryList';
import Header from './components/Header';
import KnowledgeList from './components/KnowledgeList';
import KnowledgeDialog from './components/KnowledgeDialog';
import useAddKnowledge from './hooks/useAddKnowledge';
import useCanAdmin from './hooks/useCanAdmin';
import useDeleteKnowledge from './hooks/useDeleteKnowledge';
import useEditKnowledge from './hooks/useEditKnowledge';
import useKnowledge from './hooks/useKnowledge';
import { ADMIN_MODE } from './constants';

const styles = {
  noResults: {
    fontSize: '1.5rem'
  },
  pageHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between'
  }
};

const KnowledgeCenter = () => {
  // Maintains the knowledge center administration mode setting
  const [adminMode, setAdminMode] = React.useState(null);

  // Maintains the knowledge entry id currently selected for editing/deleting
  const [knowledgeId, setKnowledgeId] = React.useState(null);

  // Determines if the user can administrate the page
  const canAdmin = useCanAdmin();

  // Retrieve the knowledge map, category list, selectedCategory state, and loading state
  const {
    categoryList,
    isLoading,
    isError,
    knowledgeMap,
    selectedCategory,
    setSelectedCategory
  } = useKnowledge();

  // Retrieve the add knowledge entry API request fucntion
  const handleAddRequest = useAddKnowledge();

  // Retrieve the edit knowledge entry API function
  const handleEditRequest = useEditKnowledge();

  // Retrieve the delete knowledge entry API function
  const handleDeleteRequest = useDeleteKnowledge({ knowledgeMap, selectedCategory, setSelectedCategory });

  /**
   * Handles actions to add knowledge entries
   * @param {object} data - form data
   */
  async function handleAdd (data) {
    // Run the request
    await handleAddRequest(data)

    // Send the user to the category of the knowledge entry just added
    setSelectedCategory(data.category);

    // Clear the admin mode
    setAdminMode(null);
  }

  /**
   * Handles actions to edit knowledge entries
   * @param {object} data - form data
   */
  async function handleEdit (data) {
    // Run the request
    await handleEditRequest({ id: knowledgeId, ...data });

    // Send the user to the category of the knowledge entry just edited (if it was moved to another category)
    setSelectedCategory(data.category);

    // Clear the admin mode and selected knowledge entry id
    setKnowledgeId(null);
    setAdminMode(null);
  }

  /**
   * Handles knowledge dialog form submissions by running the appropriate handler based on the admin mode
   * @param {object} data -form data
   */
  function handleSubmit (data) {
    try {
      if (adminMode === ADMIN_MODE.ADD) {
        return handleAdd(data);
      } else if (adminMode === ADMIN_MODE.EDIT) {
        return handleEdit(data);
      }
    } catch (_) {
      // do nothing, handled in handleAdd/EditRequest
    }

    return null;
  }

  /**
   * Handles add button clicks by setting the admin mode to ADD
   */
  function handleAddButtonClick () {
    setAdminMode(ADMIN_MODE.ADD);
  }

  /**
   * Handles edit button clicks by setting the admin mode to EDIT and setting the selected knowledge entry id
   * @param {string} id - selected konwledge entry id
   */
  function handleEditButtonClick (id) {
    setAdminMode(ADMIN_MODE.EDIT);
    setKnowledgeId(id);
  }

  /**
   * Handles delete button clicks by running the delete request
   * @param {string} id - selected knowledge entry id
   */
  function handleDeleteButtonClick (id) {
    handleDeleteRequest(id);
  }

  /**
   * Handles close dialog actions by clearing the admin mode and knowledge id
   */
  function handleCloseDialog () {
    setAdminMode(null);
    setKnowledgeId(null);
  }

  // Render the body content
  function renderContent () {
    // If there was an error when retrieving the knowledge map, display an error overlay
    if (isError) {
      return (
        <NoResults Icon={WarningAmberIcon} sx={styles.noResults} variant='overlay'>Unable to load Knowledge Center</NoResults>
      )
    }

    // If the knowledge map was retrieved by its empty, display a no results overlay
    if (knowledgeMap && Object.keys(knowledgeMap).length === 0) {
      return (
        <NoResults sx={styles.noResults} variant='overlay'>No Knowledge Entries</NoResults>
      )
    }

    // Retrieve the knowledge list from the map based on the selected category
    const list = knowledgeMap?.[selectedCategory] ?? [];

    return (
      <>
        <CategoryList
          categoryList={categoryList}
          isLoading={isLoading}
          selectedCategory={selectedCategory}
          onCategoryClick={(category) => setSelectedCategory(category)}
        />
        <KnowledgeList
          canAdmin={canAdmin}
          isLoading={isLoading}
          list={list}
          onDelete={(id) => handleDeleteButtonClick(id)}
          onEdit={(id) => handleEditButtonClick(id)}
        />
        {canAdmin && (
          <KnowledgeDialog
            mode={adminMode}
            knowledgeId={knowledgeId}
            open={adminMode !== null}
            onClose={() => handleCloseDialog()}
            onSubmit={(data) => handleSubmit(data)}
          />
        )}
      </>
    );
  }

  return (
    <BodyContainer>
      <Header canAdmin={!isError && canAdmin} onAdd={() => handleAddButtonClick()} />
      {renderContent()}
    </BodyContainer>
  );
};

export default KnowledgeCenter;
